import React, { useState, useEffect } from 'react';
import SliderIcon from '../assets/Icon/slider_icon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { logout } from '../features/auth/authSlice';
import { authService } from '../features/auth/authService';
import './SidebarLayout.css';
import Cookies from 'js-cookie';
import { hasPermission, PERMISSION_CODES, getFirstPermissionRoute } from '../utils/permissionUtils';
import { Link } from 'react-router-dom';
import { apiService } from '../services/apiService';
import signalRService from '../services/signalRService';
import infoIcon from '../assets/Icon/info_icon.svg';

const SidebarLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isMasterDataOpen, setIsMasterDataOpen] = useState(false);
  const [isAnalysisOpen, setIsAnalysisOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const [isLoadingJobStatus, setIsLoadingJobStatus] = useState(true);

  const dispatch = useDispatch(); 

  const navigate = useNavigate();
  const location = useLocation();

  // Get user from sessionStorage for permission checks
  const user = JSON.parse(sessionStorage.getItem('user'));
  const has = code => user?.permissions?.some(p => p.permission_code === code);

  // Set up SignalR connection for real-time job status updates
  useEffect(() => {
    setIsLoadingJobStatus(true);
    
    // Start SignalR connection for all pages
    signalRService.startConnection().then(success => {
      if (success) {
        console.log('SignalR connected for job status updates');
        setIsLoadingJobStatus(false);
      } else {
        console.warn('Failed to connect to SignalR');
        setIsLoadingJobStatus(false);
      }
    });

    // Listen for job status changes from SignalR
    const handleJobStatusChange = (event) => {
      const newJobStatus = event.detail;
      setJobStatus(newJobStatus);
      console.log('Job status updated via SignalR:', newJobStatus);
      console.log('Job status object structure:', JSON.stringify(newJobStatus, null, 2));
      console.log('Job status.status value:', newJobStatus?.status);
    };

    window.addEventListener('jobStatusChanged', handleJobStatusChange);

    // Cleanup function
    return () => {
      signalRService.stopConnection();
      window.removeEventListener('jobStatusChanged', handleJobStatusChange);
    };
  }, []);

  // Get username and email from sessionStorage
  const username = sessionStorage.getItem('username') || 'Admin User';
  const email = sessionStorage.getItem('email') || 'admin@aesop.com';

  const handleLogout = async () => {
    try {
      await authService.logout();
      dispatch(logout());
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if API call fails
      dispatch(logout());
      navigate('/login');
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSettingsClick = () => {
    setIsSettingsOpen((prev) => !prev);
  };

  const handleMasterDataClick = () => {
    setIsMasterDataOpen((prev) => !prev);
  };

  const handleAnalysisClick = () => {
    setIsAnalysisOpen((prev) => !prev);
  };

  const handleReportClick = () => {
    setIsReportOpen((prev) => !prev);
  };
// Price Analysis
  const CanViewPriceAnalysis = hasPermission(user, PERMISSION_CODES.PRICE_ANALYSIS_R) || hasPermission(user, PERMISSION_CODES.PRICE_ANALYSIS_ML);
  const CanViewML = hasPermission(user,PERMISSION_CODES.PRICE_ANALYSIS_ML);
  const CanViewRApplication = hasPermission(user,PERMISSION_CODES.PRICE_ANALYSIS_R);


  const CanViewBatchPricingOverride = hasPermission(user, PERMISSION_CODES.BATCH_PRICING_OVERRIDE);
  const CanViewBatchPricing = hasPermission(user, PERMISSION_CODES.BATCH_PRICING);

  // User Management
  const canViewUsers = hasPermission(user, PERMISSION_CODES.USER_VIEW) || hasPermission(user, PERMISSION_CODES.USER_MANAGEMENT);
  const canAddUsers = hasPermission(user, PERMISSION_CODES.USER_ADD) || hasPermission(user, PERMISSION_CODES.USER_MANAGEMENT);
  const canEditUsers = hasPermission(user, PERMISSION_CODES.USER_EDIT) || hasPermission(user, PERMISSION_CODES.USER_MANAGEMENT);
  const showUserManagement = canViewUsers || canAddUsers || canEditUsers;
  // Roles & Permissions
  const canViewRoles = hasPermission(user, PERMISSION_CODES.ROLE_VIEW) || hasPermission(user, PERMISSION_CODES.ROLE_MANAGEMENT);
  const canAddRoles = hasPermission(user, PERMISSION_CODES.ROLE_ADD) || hasPermission(user, PERMISSION_CODES.ROLE_MANAGEMENT);
  const canEditRoles = hasPermission(user, PERMISSION_CODES.ROLE_EDIT) || hasPermission(user, PERMISSION_CODES.ROLE_MANAGEMENT);
  const showRoles = canViewRoles || canAddRoles || canEditRoles;
  // Sites
  const canViewSites = hasPermission(user, PERMISSION_CODES.SITE_VIEW) || hasPermission(user, PERMISSION_CODES.SITE_MANAGEMENT);
  const canAddSites = hasPermission(user, PERMISSION_CODES.SITE_ADD) || hasPermission(user, PERMISSION_CODES.SITE_MANAGEMENT);
  const canEditSites = hasPermission(user, PERMISSION_CODES.SITE_EDIT) || hasPermission(user, PERMISSION_CODES.SITE_MANAGEMENT);
  const canDeleteSites = hasPermission(user, PERMISSION_CODES.SITE_DELETE) || hasPermission(user, PERMISSION_CODES.SITE_MANAGEMENT);
  const showSites = canViewSites || canAddSites || canEditSites || canDeleteSites;
  // Regions
  const canViewRegions = hasPermission(user, PERMISSION_CODES.REGION_VIEW) || hasPermission(user, PERMISSION_CODES.REGION_MANAGEMENT);
  const canAddRegions = hasPermission(user, PERMISSION_CODES.REGION_ADD) || hasPermission(user, PERMISSION_CODES.REGION_MANAGEMENT);
  const canEditRegions = hasPermission(user, PERMISSION_CODES.REGION_EDIT) || hasPermission(user, PERMISSION_CODES.REGION_MANAGEMENT);
  const canDeleteRegions = hasPermission(user, PERMISSION_CODES.REGION_DELETE) || hasPermission(user, PERMISSION_CODES.REGION_MANAGEMENT);
  const showRegions = canViewRegions || canAddRegions || canEditRegions || canDeleteRegions;
  // System Config permission
  const canViewSystemConfig = hasPermission(user, PERMISSION_CODES.SYSTEM_CONFIG_VIEW) ||hasPermission(user, PERMISSION_CODES.SYSTEM_CONFIG_PAGE);
  const canAddSystemconfig = hasPermission(user,PERMISSION_CODES.SYSTEM_CONFIG_ADD)||hasPermission(user, PERMISSION_CODES.SYSTEM_CONFIG_PAGE);
  const canupdateSystemconfig = hasPermission(user,PERMISSION_CODES.SYSTEM_CONFIG_EDIT)||hasPermission(user, PERMISSION_CODES.SYSTEM_CONFIG_PAGE);
  const candeleteSystemconfig = hasPermission(user,PERMISSION_CODES.SYSTEM_CONFIG_DELETE)||hasPermission(user, PERMISSION_CODES.SYSTEM_CONFIG_PAGE);

 const showSystemconfig = canViewSystemConfig||canAddSystemconfig ||canupdateSystemconfig || candeleteSystemconfig;
 
  const hasSystemMenu = showRoles || showUserManagement || showSystemconfig;
  // Only show Master Data section if at least one submenu is visible
  const hasMasterDataMenu = showSites || showRegions;
  // Dashboard
  const canViewDashboard = hasPermission(user, PERMISSION_CODES.DASHBOARD);
  // R Application
  //const canViewRApplication = hasPermission(user, PERMISSION_CODES.R_APPLICATION);
  // Company
  const canViewCompany = hasPermission(user, PERMISSION_CODES.COMPANY_VIEW) || hasPermission(user, PERMISSION_CODES.COMPANY_MANAGEMENT);
  const canAddCompany = hasPermission(user, PERMISSION_CODES.COMPANY_ADD) || hasPermission(user, PERMISSION_CODES.COMPANY_MANAGEMENT);
  const canEditCompany = hasPermission(user, PERMISSION_CODES.COMPANY_EDIT) || hasPermission(user, PERMISSION_CODES.COMPANY_MANAGEMENT);
  const canDeleteCompany = hasPermission(user, PERMISSION_CODES.COMPANY_DELETE) || hasPermission(user, PERMISSION_CODES.COMPANY_MANAGEMENT);
  const showCompany = canViewCompany || canAddCompany || canEditCompany || canDeleteCompany;
  
  // Reports permission check
  const canViewImpactAnalysis = hasPermission(user, PERMISSION_CODES.IMPACT_ANALYSIS);
  const canViewValidAnalysis = hasPermission(user, PERMISSION_CODES.Sufficiency_ANALYSIS);
  const canViewDemandReport = hasPermission(user, PERMISSION_CODES.DEMAND_REPORT);
  const canViewRevenue = hasPermission(user, PERMISSION_CODES.REVENUE_REPORT);
  const canViewPriceComparative =  hasPermission(user, PERMISSION_CODES.PRICE_COMPARATIVE);
  const showReports = canViewImpactAnalysis || canViewValidAnalysis || canViewDemandReport || canViewRevenue || canViewPriceComparative;
 const  ShowAllInterchange= hasPermission(user, PERMISSION_CODES.All_Interchange)
  // Global Configuration permission
  const canViewGlobalConfiguration = hasPermission(user, PERMISSION_CODES.GLOBLE_CONFIG);

  // PE Part Price permissions (you can adjust these based on your permission system)
  //const canViewPEPartPrice = hasPermission(user, PERMISSION_CODES.DASHBOARD) || true; // Default to true for now
  //const showPEPartPrice = canViewPEPartPrice;

  return (
    <div className="layout-container">
      {/* Sidebar */}
      <div className={`sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <h2>AESOP PRICING</h2>
          <button
            className="sidebar-toggle"
            onClick={toggleSidebar}
          >
            {isSidebarOpen ? '◀' : '▶'}
          </button>
        </div>

        <div className="user-info">
          <div className="user-avatar">
            {username.charAt(0)}
          </div>
          <div className="user-details">
            <p className="user-name">{username}</p>
            <p className="user-email">{email}</p>
          </div>
        </div>

        <nav className="sidebar-nav">
          {/* Company above Dashboard */}
          {showCompany && (
            <button
              className={`nav-item${location.pathname === '/company' ? ' active' : ''}`}
              onClick={() => navigate('/company')}
            >
              <span className="nav-icon">💼</span>
              {isSidebarOpen && <span className="nav-label">Company</span>}
            </button>
          )}
          {/* Analysis Dropdown Section */}
          {CanViewPriceAnalysis && (
          <div className="sidebar-section">
            <button
              className="nav-item section-toggle"
              onClick={handleAnalysisClick}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', color: 'white', padding: '12px 20px', cursor: 'pointer' }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span className="nav-icon">📊</span>
                {isSidebarOpen && <span className="nav-label">Price Analysis</span>}
              </span>
              {isSidebarOpen && (
                <span style={{ marginLeft: 'auto' }}>{isAnalysisOpen ? '▴' : '▾'}</span>
              )}
            </button>
            {isAnalysisOpen && isSidebarOpen && (
              <div className="sidebar-submenu" style={{ paddingLeft: 36 }}>
                {CanViewRApplication && (
                <button
                  className={`nav-item${location.pathname === '/r-application' ? ' active' : ''}`}
                  onClick={() => navigate('/r-application')}
                >
                  {isSidebarOpen && <span className="nav-label">Price Analysis R</span>}
                </button>
                )}
                {(CanViewML &&
                <button
                  className={`nav-item${location.pathname === '/control-bar/main' ? ' active' : ''}`}
                  onClick={() => navigate('/control-bar/main')}
                >
                  {isSidebarOpen && <span className="nav-label">Price Analysis-Model Exploration</span>}
                </button>
                )}
              </div>
            )}
          </div>
          )}

          {/* Global Configuration */}
          {canViewGlobalConfiguration && (
          <button
            className={`nav-item${location.pathname === '/global-configuration' ? ' active' : ''}`}
            onClick={() => navigate('/global-configuration')}
          >
            <span className="nav-icon">🌐</span>
            {isSidebarOpen && <span className="nav-label">Global Configuration</span>}
          </button>
          )}

             {/* batch expert queue */}
              {CanViewBatchPricingOverride && (

            <button
              className={`nav-item${location.pathname === '/batchpricingoverride' ? ' active' : ''}`}
              onClick={() => navigate('/batchpricingoverride')}
            >
              <span className="nav-icon">📝</span>
              {isSidebarOpen && <span className="nav-label">Batch Pricing Override</span>}
            </button>
          )}

          {/* Batch Pricing */}
          {CanViewBatchPricing && (
            <button
              className={`nav-item${location.pathname === '/batch-pricing' ? ' active' : ''}`}
              onClick={() => navigate('/batch-pricing')}
            >
              <span className="nav-icon">⚡</span>
              {isSidebarOpen && <span className="nav-label">Batch Pricing</span>}
            </button>
          )}

    {/* ALL Interchanges */}
              {ShowAllInterchange && (
            <button
              className={`nav-item${location.pathname === '/interchnages' ? ' active' : ''}`}
              onClick={() => navigate('/interchnages')}
            >
              <span className="nav-icon">
                <img src={SliderIcon} alt="All Interchanges" style={{ width: 20, height: 20 }} />
              </span>
              {isSidebarOpen && <span className="nav-label">ALL Interchanges</span>}
            </button>
          )}





          {/* Report Dropdown Section */}
          {showReports && (
            <div className="sidebar-section">
              <button
                className="nav-item section-toggle"
                onClick={handleReportClick}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', color: 'white', padding: '12px 20px', cursor: 'pointer' }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span className="nav-icon">📋</span>
                  {isSidebarOpen && <span className="nav-label">Report</span>}
                </span>
                {isSidebarOpen && (
                  <span style={{ marginLeft: 'auto' }}>{isReportOpen ? '▴' : '▾'}</span>
                )}
              </button>
              {isReportOpen && isSidebarOpen && (
                <div className="sidebar-submenu" style={{ paddingLeft: 36 }}>
                  {canViewImpactAnalysis && (
                    <button
                      className={`nav-item${location.pathname === '/impact-analysis' ? ' active' : ''}`}
                      onClick={() => navigate('/impact-analysis')}
                    >
                      {isSidebarOpen && <span className="nav-label">Impact Analysis</span>}
                    </button>
                  )}
                  {canViewValidAnalysis && (
                    <button
                      className={`nav-item${location.pathname === '/valid-analysis' ? ' active' : ''}`}
                      onClick={() => navigate('/valid-analysis')}
                    >
                      {isSidebarOpen && <span className="nav-label">Data Sufficiency for Analysis</span>}
                    </button>
                  )}
                  {canViewRevenue && (
                    <button
                      className={`nav-item${location.pathname === '/revenue' ? ' active' : ''}`}
                      onClick={() => navigate('/revenue')}
                     >
                      {isSidebarOpen && <span className="nav-label">Revenue</span>}
                    </button>
                  )}
                  {canViewDemandReport && (
                    <button
                      className={`nav-item${location.pathname === '/demand-report' ? ' active' : ''}`}
                      onClick={() => navigate('/demand-report')}
                    >
                      {isSidebarOpen && <span className="nav-label">Demand Report</span>}
                    </button>
                  )}
                  {canViewPriceComparative && (
                    <button
                      className={`nav-item${location.pathname === '/price-comparative' ? ' active' : ''}`}
                      onClick={() => navigate('/price-comparative')}
                    >
                      {isSidebarOpen && <span className="nav-label">Price Comparative</span>}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {/* System/Settings Section */}
          {hasSystemMenu && (
            <div className="sidebar-section">
              <button
                className="nav-item section-toggle"
                onClick={handleSettingsClick}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', color: 'white', padding: '12px 20px', cursor: 'pointer' }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span className="nav-icon">⚙️</span>
                  {isSidebarOpen && <span className="nav-label">System</span>}
                </span>
                {isSidebarOpen && (
                  <span style={{ marginLeft: 'auto' }}>{isSettingsOpen ? '▴' : '▾'}</span>
                )}
              </button>
              {isSettingsOpen && isSidebarOpen && (
                <div className="sidebar-submenu" style={{ paddingLeft: 36 }}>
                  {/* System Config now inside System section */}
                  {showSystemconfig && (
                    <button
                      className={`nav-item${location.pathname === '/system-config' ? ' active' : ''}`}
                      onClick={() => navigate('/system-config')}
                    >
                      {/* <span className="nav-icon">🛠️</span> */}
                      {isSidebarOpen && <span className="nav-label">System Config</span>}
                    </button>
                  )}
                  {showRoles && (
                    <button
                      className={`nav-item${location.pathname === '/roles-and-permissions' ? ' active' : ''}`}
                      onClick={() => navigate('/roles-and-permissions')}
                    >
                      {isSidebarOpen && <span className="nav-label"> Roles & Permissions</span>}
                     
                    </button>
                  )}
                  {showUserManagement && (
                    <button
                      className={`nav-item${location.pathname === '/add-user' ? ' active' : ''}`}
                      onClick={() => navigate('/add-user')}
                    >
                      {isSidebarOpen && <span className="nav-label">User Management</span>}
                     
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {/* Master Data Section */}
          {/* {hasMasterDataMenu && (
            <div className="sidebar-section">
              <button
                className="nav-item section-toggle"
                onClick={handleMasterDataClick}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', color: 'white', padding: '12px 20px', cursor: 'pointer' }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span className="nav-icon">🗂️</span>
                  {isSidebarOpen && <span className="nav-label">Master Data</span>}
                </span>
                {isSidebarOpen && (
                  <span style={{ marginLeft: 'auto' }}>{isMasterDataOpen ? '▴' : '▾'}</span>
                )}
              </button>
              {isMasterDataOpen && isSidebarOpen && (
                <div className="sidebar-submenu" style={{ paddingLeft: 36 }}>
                  {showSites && (
                    <button
                      className={`nav-item${location.pathname === '/sites' ? ' active' : ''}`}
                      onClick={() => navigate('/sites')}
                    >
                      Sites
                    </button>
                  )}
                  {showRegions && (
                    <button
                      className={`nav-item${location.pathname === '/regions' ? ' active' : ''}`}
                      onClick={() => navigate('/regions')}
                    >
                      Regions
                    </button>
                  )}
                </div>
              )}
            </div>
          )} */}
          {/* About at the bottom */}
          <button
            className={`nav-item${location.pathname === '/about' ? ' active' : ''}`}
            onClick={() => navigate('/about')}
          >
            <span className="nav-icon">ℹ️</span>
            {isSidebarOpen && <span className="nav-label">About</span>}
          </button>
        </nav>

        <div className="sidebar-footer">
          <button
            className="logout-button"
            onClick={handleLogout}
          >
            <span className="nav-icon">🚪</span>
            {isSidebarOpen && <span className="nav-label">Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className={`main-content ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        <header className="main-header">
          <button
            className="mobile-menu-toggle"
            onClick={toggleSidebar}
          >
            ☰
          </button>
          <h1>{
            location.pathname === '/dashboard' ? 'Dashboard' :
              location.pathname === '/company' ? 'Company' :
                location.pathname === '/system-config' ? 'System Config' :
                  // location.pathname === '/pe-part-price' ? 'PE Part Price' :
                    location.pathname === '/r-application' ? 'Price Analysis R' :
                      location.pathname === '/about' ? 'About' :
                        location.pathname === '/roles-and-permissions' ? 'Roles & Permissions' :
                          location.pathname === '/add-user' ? 'User Management' :
                            location.pathname === '/sites' ? 'Sites' :
                              location.pathname === '/regions' ? 'Regions' :
                                location.pathname === '/impact-analysis' ? 'Impact Analysis' :
                                  location.pathname === '/valid-analysis' ? 'Data Sufficiency for Analysis' :
                                  location.pathname === '/revenue' ? 'Revenue Report' :
                                  location.pathname === '/price-comparative' ? 'Price Comparative Report' :
                                    location.pathname === '/demand-report' ? 'Demand Report' :
                                    location.pathname === '/batchpricingoverride' ? 'Batch Pricing Override' :
                                    location.pathname === '/batch-pricing' ? 'Batch Pricing' :
                                    location.pathname === '/global-configuration' ? 'Global Configuration' :
                                    location.pathname === '/interchnages' ? 'ALL Interchanges' :
                                    
                                  'Price Analysis ML'
          }</h1>
                                           <div className="header-actions">
              {!isLoadingJobStatus && jobStatus && jobStatus.status === 'running' && (
                <div className="cron_job_header">
                  <img src={infoIcon} alt="Loading" className="loading-spinner" />
                  <span className="static-message">
                    {location.pathname === '/r-application' 
                      ? `Batch Pricing is in progress (${jobStatus.progressPercentage || 0}%), Please use fresh session for continued usage of R features`
                      : `Batch Pricing is in progress (${jobStatus.progressPercentage || 0}%)`
                    }
                  </span>
                </div>
              )}
            </div>
        </header>
        <main className="main-body">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default SidebarLayout; 